import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Application, ApplicationManifest } from 'models'
import { useAppDispatch } from 'store/hooks'
import { closeToast, openToast, ToastVariant } from 'store/toasts/toastSlice'
import { ModalType, openModal } from 'store/modals/modalSlice'
import { ApplicationsServiceV4 } from 'services/v4/applications-v4.service'
import { ApplicationProviderCopy, connectionErrorMessage, providerCopy } from './applicationProviders'
import {
  APPLICATIONS_PATH,
  ApplicationFormValues,
  CONNECT_FAILED_MESSAGE,
  INSTANCE_GONE_MESSAGE,
  REMOVE_FAILED_MESSAGE,
  RETRY_FAILED_MESSAGE,
  SAVE_FAILED_MESSAGE,
  freshStartPath,
  instanceScopedPath
} from './applicationSetup.constants'
import { ApplicationViewState, deriveViewState } from './applicationViewState'
import { resolveReturnedApplication } from './utils'
import { discardAbandonedApplication, forgetUnsavedApplication, rememberUnsavedApplication } from './abandonedSetup'

interface ApplicationSetup {
  manifest: ApplicationManifest | null
  application: Application | null
  copy: ApplicationProviderCopy | undefined
  viewState: ApplicationViewState
  busy: boolean
  pageError: string
  takenNames: string[]
  connect: (values: ApplicationFormValues) => Promise<void>
  retry: () => Promise<void>
  cancel: () => void
  /* Answers the sentence to show under the Name field, or an empty string when it saved. */
  save: (name: string) => Promise<string>
}

const backendMessage = (e: unknown): string => (e as any)?.response?.data?.message || ''

const isNotFound = (e: unknown): boolean => (e as any)?.response?.status === 404

const TOAST_CLOSE_DELAY_MS = 5000

/* The states in which an application exists, so leaving has something to undo. */
const CANCEL_REMOVES = [ApplicationViewState.Failed, ApplicationViewState.PendingReturn, ApplicationViewState.Configure]

/* Owns what the application setup page fetches and every write it makes, so the page component
 * only renders. */
export const useApplicationSetup = (): ApplicationSetup => {
  const { slug, applicationId: routeApplicationId } = useParams()
  const [searchParams] = useSearchParams()
  const { state: navigationState } = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const closeNotification = useRef<any>(null)

  /* What the callback page completed, so a return opens on the step the approval reached. Spent on
   * the first load: a later read, after a retry, is the current one. */
  const completed = (navigationState as { application?: Application } | null)?.application ?? null
  const completedRef = useRef<Application | null>(completed)

  /* A completion carrying a usable manifest renders the step at once; the page's own reads then
   * refresh it in the background instead of holding an empty page after the provider's reload. */
  const handedManifest = completed?.manifest?.config?.oauth_provider ? completed.manifest : null

  const [manifest, setManifest] = useState<ApplicationManifest | null>(handedManifest)
  const [application, setApplication] = useState<Application | null>(completed)
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(!handedManifest)
  const [busy, setBusy] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [pageError, setPageError] = useState('')

  /* The callback page lands on the fresh-start route carrying the id; a reload arrives with it in
   * the path. Both mean the same instance. */
  const applicationId = routeApplicationId || searchParams.get('application_id') || undefined
  const callbackError = searchParams.get('error')

  /* The close is deliberately left running when the page goes: saving raises its toast on the way
   * out, and a cleared timer is what left that one on screen for good. */
  const notify = useCallback(
    (variant: ToastVariant, text: string, description = '') => {
      clearTimeout(closeNotification.current)
      dispatch(openToast({ variant, props: { text, description } }))
      closeNotification.current = setTimeout(() => dispatch(closeToast()), TOAST_CLOSE_DELAY_MS)
    },
    [dispatch]
  )

  const loadPage = useCallback(async () => {
    if (!slug) {
      setLoading(false)
      return
    }

    const handedOver = completedRef.current
    completedRef.current = null
    const refreshing = !!handedOver?.manifest?.config?.oauth_provider
    if (!refreshing) setLoading(true)
    setNotFound(false)

    /* First, so an instance abandoned on the way back here neither lists nor blocks its own name. */
    await discardAbandonedApplication(applicationId)

    /* Asked for together: run one after another and the return from the provider spends a second
     * on a page with nothing on it. */
    const [manifestResult, applicationsResult, applicationResult] = await Promise.allSettled([
      ApplicationsServiceV4.getManifest(slug),
      ApplicationsServiceV4.listLiveApplications(),
      applicationId ? ApplicationsServiceV4.getApplication(applicationId) : Promise.resolve(null)
    ])

    if (manifestResult.status === 'rejected') console.error(manifestResult.reason)
    const fallbackManifest = refreshing ? handedOver?.manifest ?? null : null
    setManifest(manifestResult.status === 'fulfilled' ? manifestResult.value ?? fallbackManifest : fallbackManifest)

    /* The duplicate check is a courtesy before the redirect; the create route decides. A list
     * that fails to load must not keep the admin off the page. */
    if (applicationsResult.status === 'rejected') console.error(applicationsResult.reason)
    setApplications(applicationsResult.status === 'fulfilled' ? applicationsResult.value : [])

    if (!applicationId) {
      setApplication(null)
    } else if (applicationResult.status === 'fulfilled') {
      setApplication(resolveReturnedApplication(applicationResult.value, handedOver))

      /* Carry the instance in the path so a refresh resumes, using the history API rather than
       * router navigation so the page is not remounted mid-return. */
      if (!routeApplicationId) {
        window.history.replaceState(null, '', instanceScopedPath(slug, applicationId))
      }
    } else {
      console.error(applicationResult.reason)
      setApplication(null)

      if (isNotFound(applicationResult.reason)) {
        setNotFound(true)
        notify(ToastVariant.error, INSTANCE_GONE_MESSAGE)
        navigate(freshStartPath(slug))
      }
    }

    setLoading(false)
  }, [slug, applicationId, routeApplicationId, navigate, notify])

  useEffect(() => {
    loadPage().catch((e) => e)
  }, [loadPage])

  const copy = providerCopy(manifest?.config?.oauth_provider)

  /* The callback's query is a hint, never the state: it only picks the toast the admin sees on
   * arrival, and it is gone on the next reload while the connection block is not. */
  const greeted = useRef(false)
  useEffect(() => {
    if (loading || greeted.current || !callbackError) return

    greeted.current = true
    notify(ToastVariant.error, connectionErrorMessage(copy, callbackError))
  }, [loading, callbackError, copy, notify])

  /* Its own name is not a duplicate of itself, so the instance being configured is left out. */
  const takenNames = useMemo(
    () => applications.filter((entry) => entry.id !== application?.id).map((entry) => entry.display_name),
    [applications, application]
  )

  const viewState = deriveViewState({ loading, redirecting, notFound, manifest, applicationId, application })

  const connect = async (values: ApplicationFormValues) => {
    if (!manifest) return

    setPageError('')
    setBusy(true)

    try {
      const { application: created, authorization_url: authorizationUrl } =
        await ApplicationsServiceV4.createApplication({
          slug: manifest.slug,
          display_name: values.name,
          description: values.description || undefined
        })
      if (created?.id) rememberUnsavedApplication(created.id)

      /* Only ever the address the backend answered with; the page never builds a provider URL. */
      setRedirecting(true)
      window.location.assign(authorizationUrl)
    } catch (e) {
      console.error(e)
      setPageError(backendMessage(e) || CONNECT_FAILED_MESSAGE)
      setBusy(false)
    }
  }

  /* A refused retry usually means the connection moved under the admin, so the block is re-read
   * rather than the whole page, which would blank the step it is standing on. */
  const reloadApplication = async () => {
    if (!application) return

    try {
      setApplication(await ApplicationsServiceV4.getApplication(application.id))
    } catch (e) {
      console.error(e)
    }
  }

  const retry = async () => {
    if (!application) return

    setPageError('')
    setBusy(true)

    try {
      const { authorization_url: authorizationUrl } = await ApplicationsServiceV4.retryApplication(application.id)
      rememberUnsavedApplication(application.id)

      setRedirecting(true)
      window.location.assign(authorizationUrl)
    } catch (e) {
      console.error(e)
      setPageError(backendMessage(e) || RETRY_FAILED_MESSAGE)
      setBusy(false)
      await reloadApplication()
    }
  }

  const save = async (name: string): Promise<string> => {
    if (!application || !manifest) return ''

    setBusy(true)

    try {
      if (name !== application.display_name) {
        await ApplicationsServiceV4.renameApplication(application.id, name)
      }

      forgetUnsavedApplication()
      notify(ToastVariant.success, `${manifest.display_name} integration instance added`, name)
      navigate(APPLICATIONS_PATH)

      return ''
    } catch (e) {
      console.error(e)

      return backendMessage(e) || SAVE_FAILED_MESSAGE
    } finally {
      setBusy(false)
    }
  }

  /* Remove cancels the grant at the provider, drops the tokens and hides the instance, so it is
   * only ever reached through the confirmation below. */
  const remove = async () => {
    if (!application) return

    setBusy(true)

    try {
      await ApplicationsServiceV4.removeApplication(application.id)
      forgetUnsavedApplication()
      navigate(APPLICATIONS_PATH)
    } catch (e) {
      console.error(e)
      notify(ToastVariant.error, REMOVE_FAILED_MESSAGE, backendMessage(e))
    } finally {
      setBusy(false)
    }
  }

  /* Before the redirect nothing exists, so Cancel is just a way out. Afterwards the row is real
   * and Onyxia may be an approved app at the provider, so leaving has to undo both. */
  const cancel = () => {
    if (!CANCEL_REMOVES.includes(viewState)) {
      navigate(APPLICATIONS_PATH)
      return
    }

    dispatch(openModal({ type: ModalType.cancelSetupIntegrationChanges, props: { okCallback: remove } }))
  }

  return { manifest, application, copy, viewState, busy, pageError, takenNames, connect, retry, cancel, save }
}
